import { Icon } from '@/components'
import { URL_IMG } from '@/constants/ui'
import { useCountdownV2 } from '@/hooks'
import { TypePopup, useUpsellStore } from '@/store/upsell'
import { getVND } from '@/utils/helpers'
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material'
import Image from 'next/image'
import { useEffect, useMemo } from 'react'
import { useSubmitOffers } from '../hooks/use-submit-offers'
import { UpsellIcon } from './icon'
import { ProductItem } from './wrap-global-fixed-product'
import { useUpsellCountdown } from '../hooks'
type PopupUpsellProps = {
  isOpenPopup: boolean
  onClose: () => void
  data: IOfferOrderResponse | undefined
}

const commonColor = '#FF421D'
export const PopupUpsell = ({ isOpenPopup, onClose, data }: PopupUpsellProps) => {
  const { setOrderCode } = useUpsellStore()

  const { expireAfter: count } = useUpsellCountdown({
    type: TypePopup.UPSELL,
    expireDate: data?.expire_after_date as string,
    onComplete: onClose,
  })


  const total = useMemo(() => {
    return data?.offers?.reduce((acc, offer) => {
      return acc + (offer?.final_price || 0) * (offer?.add_qty || 0)
    }, 0) || 0
  },[data?.offers])

  useEffect(() => {
    if (data?.order_id) {
      setOrderCode(data?.order_id.toString(), TypePopup.UPSELL)
    }
  }, [setOrderCode, data?.order_id])

  const { handleSubmit, isPending } = useSubmitOffers(data)
  const caculatedCashbackCoin = useMemo(() => {
    const cashback = data?.affiliate_information?.cashback
    if (!cashback || !total) return 0
    const cashbackCoin = Math.ceil((cashback * total) / 100)
    return cashbackCoin
  }, [data?.affiliate_information?.cashback, total])
  return (
    <Dialog
      open={isOpenPopup}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '1056px',
          borderRadius: '8px',
        },
      }}
    >
      <DialogContent sx={{ padding: 2, position: 'relative' }}>
        <IconButton
          sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}
          onClick={onClose}
          size="small"
        >
          <Icon.Close sx={{ fontSize: '1rem', color: 'primary.main' }} />
        </IconButton>
        <Stack gap={2}>
          <Stack gap={'12px'}>
            <Stack gap={1} direction={'row'} alignItems="center">
              <UpsellIcon.Fire sx={{ fontSize: 24 }} />
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem', lg: '1.5rem' },
                  fontWeight: 600,
                  color: commonColor,
                }}
              >
                Khuyến mãi đặc biệt giới hạn
              </Typography>
            </Stack>
            <Stack
              gap={1}
              alignItems={'center'}
              direction={'row'}
              sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, fontWeight: 300 }}
            >
              <Box>Bạn chỉ còn</Box>
              <Box
                sx={{
                  background: commonColor,
                  color: 'primary.contrastText',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                }}
              >
                {/* make the animation shake */}
                <UpsellIcon.Clock
                  sx={{
                    fontSize: 18,
                    color: 'inherit',
                    transition: 'transform 0.3s',
                    animation: 'shake 2s infinite',
                    '@keyframes shake': {
                      '0%': { transform: 'rotate(10deg)' },
                      '50%': { transform: 'rotate(-10deg)' },
                      '70%': { transform: 'rotate(10deg)' },
                      '100%': { transform: 'rotate(0deg)' },
                    },
                  }}
                />
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.25rem', lg: '1.5rem' },
                    lineHeight: 1,
                  }}
                >
                  {Math.floor(count / 60)
                    .toString()
                    .padStart(2, '0')}
                  :{(count % 60).toString().padStart(2, '0')}
                </Box>
              </Box>
              <Box>để nhận ưu đãi này!</Box>
            </Stack>
          </Stack>
          <Stack gap={1}>
            {data?.offers?.map((item, index) => {
              const convertedData = {
                product_name: item?.product_name,
                add_qty: item?.add_qty,
                final_price: item?.final_price,
                checkout_price: item?.checkout_price,
                product_image: item?.product_image,
                link: '#',
                attributes: item?.attributes || [],
              }
              return <ProductItem key={index} item={convertedData} />
            })}
          </Stack>
          <Stack
            alignItems={'center'}
            direction={'row'}
            gap={1}
            sx={{
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              color: commonColor,
            }}
          >
            <Icon.DiscountV2 sx={{ fontSize: '1.125em', color: 'inherit' }} />

            <Typography fontWeight={600} sx={{ color: 'inherit', fontSize: 'inherit' }}>
              Bạn chỉ cần bỏ ra thêm {getVND(total, 'Đ')} để sỡ hữu thêm khuyến mãi này
            </Typography>
          </Stack>
          <ButtonSubmit
            cashbackCoin={caculatedCashbackCoin}
            total={total}
            order="12345"
            onSubmit={handleSubmit}
            loading={isPending}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

//#region Button Submit
const ButtonSubmit = ({
  cashbackCoin,
  total,
  order,
  onSubmit,
  loading,
}: {
  cashbackCoin: number
  total: number
  order: string
  onSubmit: () => void
  loading: boolean
}) => {
  return (
    <Button
      loading={loading}
      disabled={loading}
      onClick={onSubmit}
      variant="contained"
      sx={{
        width: '100%',
        height: '100%',
        padding: '12px 24px',
        color: 'primary.contrastText',
        textTransform: 'none',
        '&.Mui-disabled': {
          backgroundColor: 'primary.main',

          '& img': {
            opacity: 0.5,
          },
        },
        '&.MuiButton-loading': {
          '& img': {
            opacity: 0,
          },
        },
      }}
    >
      <Stack>
        <Typography
          sx={{ fontSize: { xs: '0.875rem', md: '1rem', xl: '1.125rem' }, color: 'inherit', fontWeight: 600 }}
        >
          THANH TOÁN NGAY - {getVND(total, 'Đ')}
        </Typography>
        {cashbackCoin > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              component={'span'}
              sx={{
                fontSize: { xs: '12px', md: '14px' },
                lineHeight: 1.3,
                color: 'inherit',
                fontWeight: 300,
                textAlign: 'center',
              }}
            >
              Nhận ngay{' '}
              <Box component={'span'} sx={{ fontWeight: 600 }}>
                {getVND(cashbackCoin, ' ')}
              </Box>{' '}
              xu
            </Typography>
            <Image
              style={{ display: 'inline-block', margin: '0 4px' }}
              width={16}
              height={16}
              src={URL_IMG + 'favicon-yellow.svg'}
              alt="Favicon"
            />
          </Box>
        )}
      </Stack>
    </Button>
  )
}
